import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    MenuItem,
    Paper,
    Slide,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import baseService from '../services/BaseService'; // Adjust the import based on your file structure

const BaseList = ({ clientId: clientIdProp }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const clientId = clientIdProp ?? params?.clientId;

    const [bases, setBases] = useState([]);
    const [newBaseId, setNewBaseName] = useState(''); // used when vendor is VectorShift
    const [projectId, setProjectId] = useState('');   // used when vendor is VoiceFlow
    const [vendor, setVendor] = useState('VectorShift');
    const [apiKey, setApiKey] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' | 'error' | 'info' | 'warning'

    // Delete confirmation dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // { s3_path, base_name, base_id, project_id, vendor }

    const SlideTransition = (props) => <Slide {...props} direction="up" />;
    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const fetchBases = useCallback(async () => {
        try {
            console.log('clientId:', clientId);
            const data = await baseService.getBases(clientId);
            setBases(data);
        } catch (error) {
            console.error('Error fetching bases:', error);
        }
    }, [clientId]);

    useEffect(() => {
        if (!clientId) return;
        fetchBases();
    }, [clientId, fetchBases]);

    const handleAddBase = async () => {
        const isVoiceFlow = vendor === 'VoiceFlow';
        const idValue = isVoiceFlow ? projectId : newBaseId;
        if (!idValue || idValue.trim() === '') {
            setSnackbarMessage(isVoiceFlow ? t('baseList.projectIdRequired', 'Project ID is required') : t('baseList.baseIdRequired', 'Base ID is required'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        try {
            const newBase = isVoiceFlow
                ? {
                    clientId,
                    vendor,
                    projectId,
                    apiKey,
                }
                : {
                    clientId,
                    vendor,
                    baseId: newBaseId,
                };
            await baseService.addBase(newBase);
            setNewBaseName('');
            setProjectId('');
            setApiKey('');
            fetchBases();
            setSnackbarMessage(t('baseList.baseAdded', 'Base added successfully'));
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error adding base:', error);
            setSnackbarMessage(error?.message || t('baseList.addFailed', 'Failed to add base'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleDeleteBase = async (s3_path) => {
        try {
            await baseService.deleteBase(clientId, s3_path);
            fetchBases();
            setSnackbarMessage(t('baseList.baseDeleted', 'Base deleted successfully'));
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error deleting base:', error);
            setSnackbarMessage(error?.message || t('baseList.deleteFailed', 'Failed to delete base'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const promptDelete = (base) => {
        setDeleteTarget(base);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        await handleDeleteBase(deleteTarget.s3_path);
        setConfirmOpen(false);
        setDeleteTarget(null);
    };

    const handleCancelDelete = () => {
        setConfirmOpen(false);
        setDeleteTarget(null);
    };

    const formatFolderSize = (size) => {
        if (size == null || isNaN(size)) return '';
        const s = Number(size);
        if (s < 1024) return `${s} B`;
        if (s < 1024 * 1024) return `${(s / 1024).toFixed(0)} KB`;
        if (s < 1024 * 1024 * 1024) return `${(s / (1024 * 1024)).toFixed(1)} MB`;
        return `${(s / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2,
                }}
            >
                <Box>
                    <Breadcrumbs aria-label={t('baseList.breadcrumb', 'breadcrumb')} sx={{ mb: 0.5 }}>
                        <Link
                            component={RouterLink}
                            underline="hover"
                            color="inherit"
                            to="/customer-list"
                            sx={{ fontSize: 13 }}
                        >
                            {t('baseList.clientList', 'Client List')}
                        </Link>
                        <Typography color="text.primary" sx={{ fontSize: 13 }}>
                            {t('baseList.bases', 'Bases')}
                        </Typography>
                    </Breadcrumbs>
                    <Typography variant="h5" fontWeight="bold">
                        {t('baseList.baseListTitle', 'Base List')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('baseList.client', 'Client: {{id}}', { id: clientId || '-' })}
                    </Typography>
                </Box>

                <Button variant="outlined" onClick={() => navigate('/customer-list')}>
                    {t('baseList.backToClients', 'Back to Clients')}
                </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small" aria-label={t('baseList.basesTable', 'bases table')}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('baseList.name', 'Name')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('baseList.projectIdKbId', 'Project ID/ KB ID')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('baseList.vendor', 'Vendor')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('baseList.folderSize', 'Folder Size')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('baseList.actions', 'Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bases && bases.length > 0 ? (
                            bases.map((base) => (
                                <TableRow key={base.s3_path} hover>
                                    <TableCell>{base.base_name}</TableCell>
                                    <TableCell>{base.base_id ?? base.project_id}</TableCell>
                                    <TableCell>{base.vendor ?? ''}</TableCell>
                                    <TableCell>{formatFolderSize(base.folder_size)}</TableCell>
                                    <TableCell align="right">
                                        <IconButton aria-label={t('baseList.deleteAria', 'delete')} onClick={() => promptDelete(base)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                                    {t('baseList.noBases', 'No bases yet')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box display="flex" alignItems="flex-start" gap={2} sx={{ mt: 0, mb: 0 }}>
                {/* Left column: Project/Base ID and conditional API Key */}
                <Box display="flex" flexDirection="column" gap={1}>
                    <TextField
                        variant="outlined"
                        label={vendor === 'VoiceFlow' ? t('baseList.projectId', 'Project ID') : t('baseList.baseId', 'Base ID')}
                        value={vendor === 'VoiceFlow' ? projectId : newBaseId}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            // Match the button height and make the font larger
                            '& .MuiOutlinedInput-root': {
                                height: 40,
                                padding: 0,
                                '& input': {
                                    height: '100%',
                                    padding: '0 12px',
                                    fontSize: '1.1rem',
                                    boxSizing: 'border-box'
                                }
                            }
                        }}
                        onChange={(e) => (vendor === 'VoiceFlow' ? setProjectId(e.target.value) : setNewBaseName(e.target.value))}
                    />
                    {vendor === 'VoiceFlow' && (
                        <TextField
                            variant="outlined"
                            type="password"
                            label={t('baseList.apiKey', 'API Key')}
                            value={apiKey}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                    padding: 0,
                                    '& input': {
                                        height: '100%',
                                        padding: '0 12px',
                                        fontSize: '1.1rem',
                                        boxSizing: 'border-box'
                                    }
                                }
                            }}
                            onChange={(e) => setApiKey(e.target.value)}
                        />
                    )}
                </Box>
                {/* Right side: vendor and submit */}
                <TextField
                    select
                    variant="outlined"
                    label={t('baseList.vendor', 'Vendor')}
                    value={vendor}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                        const v = e.target.value;
                        setVendor(v);
                        if (v !== 'VoiceFlow') setApiKey('');
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: 40,
                            padding: 0,
                            '& .MuiSelect-select': {
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 12px',
                                fontSize: '1.1rem',
                                boxSizing: 'border-box'
                            }
                        }
                    }}
                >
                    <MenuItem value="VectorShift">VectorShift</MenuItem>
                    <MenuItem value="VoiceFlow">VoiceFlow</MenuItem>
                </TextField>
                <Button variant="contained" color="primary" onClick={handleAddBase}>
                    {t('baseList.addBase', 'Add Base')}
                </Button>
            </Box>
            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
                <DialogTitle>{t('baseList.deleteBaseTitle', 'Delete Base')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {t('baseList.deleteConfirm', 'Are you sure you want to delete this base?')}
                    </Typography>
                    {deleteTarget && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">{deleteTarget.base_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {(deleteTarget.base_id ?? deleteTarget.project_id) ? t('baseList.idLabel', 'ID: {{id}}', { id: deleteTarget.base_id ?? deleteTarget.project_id }) : ''}
                                {deleteTarget.vendor ? t('baseList.vendorLabel', '  •  Vendor: {{vendor}}', { vendor: deleteTarget.vendor }) : ''}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>{t('baseList.cancel', 'Cancel')}</Button>
                    <Button color="error" variant="contained" onClick={handleConfirmDelete}>{t('baseList.delete', 'Delete')}</Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                TransitionComponent={SlideTransition}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BaseList;